import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  computed,
  effect,
  EventEmitter,
  HostListener,
  Input,
  input,
  OnChanges,
  OnDestroy,
  Output,
  output,
  signal,
  SimpleChanges,
  WritableSignal,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core'
import { ListingItem } from 'depoto-core/src/models'
import { CoreService } from '../../../services'
import { StorageKeyService } from '../../../services/storage-key.service'
import { File } from 'depoto-core/src/entities'
import { TranslateService } from '@ngx-translate/core'
type StorageKey = { type: string; key: string }

@Component({
  selector: 'app-base-listing',
  templateUrl: './base-listing.component.html',
  styleUrls: ['./base-listing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BaseListingComponent implements OnDestroy, OnChanges, AfterViewInit {
  // listName = input.required<string>()
  listName = input<string>('')
  exportsEntity = input<string>('')
  needsTranslationPrefix = input<boolean>(false)
  // todo ziskavat listing columns podle name entity

  // todo required?
  columns = input<WritableSignal<any[]>>(signal([]))
  isColumnSettingVisible = input<boolean>(false)
  totalItems = input<number>(0)
  filters = input<any>() // todo type filters

  // query = input.required<CreateInfiniteQueryResult<InfiniteData<any>, Error>>()
  query = input.required<any>()
  checkedMap = input<WritableSignal<Record<string, boolean>>>()

  loadStringifiedFilters = input<boolean>(false)

  // Show full overlay only while the first data load hasn't produced any items yet.
  // Once any data is present, do not show the full overlay even if refetching/pending toggles.
  loadingFirstPage = computed(() => {
    const q = this.query()
    return !q.isFetchingNextPage() && q.isFetching()
  })
  loadingNextPage = computed(() => this.query().isFetchingNextPage())
  // Keep the pagination area visible while the next page is loading so the spinner is shown
  // Previously this was hidden during any fetching which caused a visual gap.
  nextButtonShow = computed(() => this.query().hasNextPage())

  sortProp = input<WritableSignal<string>>(signal<string>('dateCreated'))
  sortDir = input<WritableSignal<'asc' | 'desc'>>(signal<'asc' | 'desc'>('asc'))

  @Input() areAllOrdersCheckboxChecked = false
  @Output() areAllOrdersCheckboxCheckedChange = new EventEmitter<boolean>()
  areAllOrdersCheckedChange = output<boolean>()

  stringifiedFilters = input<string>('')
  storageKey = input<StorageKey>({ type: '', key: '' })

  // todo required ???
  // selectedColumns = input<WritableSignal<ListingItem[]>>(this.columns())
  selectedColumns = input.required<WritableSignal<ListingItem[]>>()

  noSelectedColumns = computed(() => this.selectedColumns()()?.length <= 1)

  callAction = output<any>()

  public showTopScroll = false
  public spacerWidth = 0
  private syncingScroll = false
  @ViewChild('topScroll') topScroll: ElementRef<HTMLDivElement>
  @ViewChild('tableWrap') tableWrap: ElementRef<HTMLDivElement>

  priorities: any[] = [
    { id: 0, name: 'priority.lowest' },
    { id: 1, name: 'priority.low' },
    { id: 10, name: 'priority.normal' },
    { id: 20, name: 'priority.high' },
    { id: 30, name: 'priority.highest' },
  ]

  constructor(
    public core: CoreService,
    public keyService: StorageKeyService,
    public translateService: TranslateService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    // items/columns are signals, ngOnChanges never sees them load - re-measure the top scrollbar after each render
    effect(() => {
      this.items()
      this.loadingFirstPage()
      this.selectedColumns()()
      setTimeout(() => this.updateTopScrollState())
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.storageKey?.currentValue) {
      this.loadSettings()
    }
    // Recompute overflow when items/columns change
    if (changes.items || changes.columns || changes.storageKey) {
      // allow view to update first
      setTimeout(() => this.updateTopScrollState())
    }
  }

  loadDefaultCols() {
    this.selectedColumns().set([...this.columns()().filter(c => c.isDefault)])
  }

  saveSettings(): void {
    this.core.services.storage.set(this.storageKey(), {
      selectedColumns: this.selectedColumns()().map(c => c.name),
      filters: this.filters() ? JSON.stringify(this.filters()) : undefined,
      sortProp: this.sortProp()(),
      sortDir: this.sortDir()(),
    })
  }

  /**
   * Convert export filters into human readable label/value pairs.
   */
  formatFilters(filter: any): { label: string; value: string }[] {
    const out: { label: string; value: string }[] = []
    if (!filter) return out

    let f: any = filter
    if (typeof f === 'string') {
      try {
        f = JSON.parse(f)
      } catch {
        // not JSON, show raw
        out.push({ label: 'Filtr', value: String(f) })
        return out
      }
    }

    const entries = Object.entries(f as Record<string, any>)
    for (const [rawKey, value] of entries) {
      const baseLabel = this.translateService.instant(`exports-filters.${this.exportsEntity()}.${rawKey}`)

      // Date range object: { left_date: string, right_date: string }
      if (value && typeof value === 'object' && ('left_date' in value || 'right_date' in value)) {
        const left = value.left_date ? this.formatDate(value.left_date) : ''
        const right = value.right_date ? this.formatDate(value.right_date) : ''
        if (left) out.push({ label: `${baseLabel} - Od`, value: left })
        if (right) out.push({ label: `${baseLabel} - Do`, value: right })
        // if (String(baseLabel) === 'Datum splatnosti dokladu') { todo
        //   const lala = value.right_date instanceof Date ? value.right_date : new Date(value.right_date)
        // }
        continue
      }

      // Simple boolean
      if (typeof value === 'boolean') {
        if (String(this.translateService.currentLang) === 'cs') {
          out.push({ label: baseLabel, value: value ? 'Ano' : 'Ne' })
          continue
        } else {
          out.push({ label: baseLabel, value: value ? 'Yes' : 'No' })
          continue
        }
      }

      if (value === 'true' || value === 'false') {
        if (String(this.translateService.currentLang) === 'cs') {
          out.push({ label: baseLabel, value: value === 'true' ? 'Ano' : 'Ne' })
          continue
        }
        out.push({ label: baseLabel, value: value === 'true' ? 'Yes' : 'No' })
        continue
      }

      // IMPORTANT: if adding exports for entity, need to add also transform/translation for its filters
      if (baseLabel === 'Status') {
        out.push({ label: baseLabel, value: this.translateService.instant(String(value)) })
        continue
      } else if (baseLabel === 'Procesní stav') {
        out.push({ label: baseLabel, value: this.translateService.instant(String(value)) })
        continue
      } else if (baseLabel === 'Dostupnost') {
        out.push({ label: baseLabel, value: this.translateService.instant(String(value)) })
        continue
      } else if (baseLabel === 'Typ') {
        out.push({
          label: baseLabel,
          value: this.translateService.instant(`exports-filters.${this.exportsEntity()}.${String(value)}`),
        })
        continue
      } else if (baseLabel === 'Typ slevy') {
        out.push({
          label: baseLabel,
          value: this.translateService.instant(`exports-filters.${this.exportsEntity()}.${String(value)}`),
        })
        continue
      } else if (baseLabel === 'Typ operace') {
        out.push({
          label: baseLabel,
          value: this.translateService.instant(`exports-filters.${this.exportsEntity()}.${String(value)}`),
        })
        continue
      }

      // Arrays -> comma separated
      if (Array.isArray(value)) {
        out.push({ label: baseLabel, value: value.map(v => this.valueToString(v)).join(', ') })
        continue
      }

      // Dates as ISO strings?
      if (this.looksLikeDate(value)) {
        out.push({ label: baseLabel, value: this.formatDate(value) })
        continue
      }

      // Fallback
      out.push({ label: baseLabel, value: this.valueToString(value) })
    }

    return out
  }

  private valueToString(v: any): string {
    if (v == null) return ''
    if (typeof v === 'object') return JSON.stringify(v)
    return String(v)
  }

  private looksLikeDate(v: any): boolean {
    if (typeof v !== 'string') return false
    // Basic ISO-like check
    return /\d{4}-\d{2}-\d{2}/.test(v)
  }

  private formatDate(d: string | Date): string {
    const date = d instanceof Date ? d : new Date(d)
    if (isNaN(date.getTime())) {
      const dateWithoutTime = String(d).split('T')[0]
      return this.valueToString(dateWithoutTime)
    }
    const day = date.getDate()
    const month = date.getMonth() + 1
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }

  onAction(action: string, item: any) {
    this.callAction.emit({
      action: action,
      item: item,
    })
  }

  // Flatten query data (supports InfiniteData, { items }, or plain arrays)
  items = computed<any[]>(() => {
    const q = this.query?.()
    const data = q?.data?.()
    // Plain array
    if (Array.isArray(data)) return data

    // InfiniteQuery shape { pages: [...] }
    if (data?.pages && Array.isArray(data.pages)) {
      return data.pages.flatMap((p: any) => (Array.isArray(p?.items) ? p.items : []))
    }

    // Object with items
    if (Array.isArray(data?.items)) return data.items

    return []
  })

  loadSettings(): void {
    const view = this.core.services.storage.getSync(this.storageKey())
    if (this.isColumnSettingVisible()) {
      if (view?.selectedColumns?.length) {
        this.selectedColumns().set(this.columns()().filter(c => view.selectedColumns.some(col => col === c.name)))
      } else {
        this.selectedColumns().set([...this.columns()().filter(c => c.isDefault)])
      }
    } else {
      this.selectedColumns().set([...this.columns()()])
    }
    // const savedColumns = this.core.services.storage.getSync(this.storageKey())
    // if (savedColumns?.orders) {
    //   savedColumns.orders = savedColumns.orders.filter(savedColumn =>
    //     this.columns()().find(column => column.name === savedColumn.name)
    //   )
    // }
    // if (this.isColumnSettingVisible()) {
    //   this.selectedColumns().set([...this.columns()().filter(c => c.isDefault)])
    // } else {
    //   this.selectedColumns().set([...this.columns()()])
    // }
    // if (savedColumns && savedColumns[this.listName()]?.length) {
    //   this.selectedColumns().set(
    //     savedColumns[this.listName()].map(c => this.columns()().filter(col => col.name === c.name)[0])
    //   )
    // }
    // this.saveSettings()
  }

  ngOnDestroy() {
    window.onscroll = null
  }

  ngAfterViewInit(): void {
    // Initial computation after view is ready
    setTimeout(() => this.updateTopScrollState())
  }

  @HostListener('window:resize')
  onResize() {
    this.updateTopScrollState()
  }

  private updateTopScrollState(): void {
    const wrap = this.tableWrap?.nativeElement
    if (!wrap) return
    const scrollWidth = wrap.scrollWidth || 0
    const clientWidth = wrap.clientWidth || 0
    this.spacerWidth = scrollWidth
    this.showTopScroll = scrollWidth > clientWidth + 1
    this.changeDetectorRef.markForCheck()
  }

  syncScroll(source: 'top' | 'bottom'): void {
    if (this.syncingScroll) return
    const top = this.topScroll?.nativeElement
    const bottom = this.tableWrap?.nativeElement
    if (!top || !bottom) return
    this.syncingScroll = true
    if (source === 'top') {
      bottom.scrollLeft = top.scrollLeft
    } else {
      top.scrollLeft = bottom.scrollLeft
    }
    this.syncingScroll = false
  }

  getStorageKeyForEntity(entity: string): StorageKey {
    return this.keyService.getStorageKeyForEntity(entity, 'baseListing')
  }

  sortBy(property: string): void {
    if (this.sortProp()() === property) {
      const newDir = this.sortDir()() === 'asc' ? 'desc' : 'asc'
      this.sortDir().set(newDir)
    } else {
      this.sortProp().set(property)
    }
    this.core.services.storage.set(this.storageKey(), {
      selectedColumns: this.selectedColumns()().map(c => c.name),
      filters: this.filters() ? JSON.stringify(this.filters()) : undefined,
      sortProp: this.sortProp()(),
      sortDir: this.sortDir()(),
    })

    this.query().refetch()
  }

  onColumnSettingDropdownClick(event: string) {
    const isSelected = this.selectedColumns()().filter(c => c.name === event).length
    // console.warn(event, isSelected, [...this.selectedColumns], [...this.columns]); // TODO: remove
    if (isSelected) {
      this.selectedColumns().set(this.selectedColumns()().filter(c => c.name !== event))
    } else {
      const cols = this.columns()().find(c => String(c.name) === String(event))
      const allCols = [...this.selectedColumns()(), cols]
      // sorting by reference arr (includes() breaks if isSelected - different objects..):
      this.selectedColumns().set(this.columns()().filter(c => allCols.some(aC => aC.name === c.name))) // todo
    }

    this.saveSettings()
    this.query().refetch()
    setTimeout(() => this.updateTopScrollState())
  }

  public trackByColumnName(index: number, column: ListingItem): string {
    // if (column.name === 'orders-checkbox') console.error(column.name)
    return column.name
  }

  public trackByIndex(index: number) {
    return index
  }

  public trackByItemId(index: number, item: any): number | string {
    return item?.id ?? index
  }

  // todo
  // onCheckboxChange(itemId, value) {
  //   if (itemId === 0) {
  //     this.items().forEach((it, i) => {
  //       this.items()[i].isSelected = value
  //     })
  //   }
  // }

  allOrdersChecked(areAllOrdersChecked: boolean) {
    this.areAllOrdersCheckboxCheckedChange.emit(areAllOrdersChecked)
    this.areAllOrdersCheckedChange.emit(areAllOrdersChecked)
  }

  orderWasChecked(orderId: string, checked: boolean) {
    const mapSignal = this.checkedMap()
    if (!mapSignal) return // ignore in pages where checked isn't used
    if (!checked) {
      this.areAllOrdersCheckboxCheckedChange.emit(false)
    }
    mapSignal.update(map => ({
      ...map,
      [orderId]: checked,
    }))
  }

  isOrderChecked(orderId: string | number): boolean {
    if (orderId == null) return false
    const mapSignal = this.checkedMap()
    return !!mapSignal?.()[orderId]
  }

  getPriorityName(priorityId: number): string | undefined {
    return this.priorities.find(p => p.id === priorityId)?.name
  }

  getThumbUrl(image: File): string {
    return image.thumbnails.filter(t => t.format === 'w135wp')[0].url
  }
}
